import { useModel as _useModel, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "wrapper" }
const _hoisted_2 = { class: "district-view__title" }
const _hoisted_3 = { class: "district-view__name" }

import {ref, computed, onBeforeUnmount, watch, nextTick} from "vue";
import PerfectScrollbar from "perfect-scrollbar";
import IconChevronBottom from "@/assets/icons/IconChevronBottom.vue";
import IconChevronRight from "@/assets/icons/IconChevronRight.vue";
import {districtList} from "@/composables/list/district.list";
import {getRegionsByDistrict} from "@/composables/functions/getRegionByDistrict";
import {getDistrictName, type DistrictKey} from "@/composables/functions/getDistrictName";
import {getRegionName, type RegionKey} from "@/composables/functions/getRegionName";
import {citiesList} from "@/composables/list/cities.list";
import {regionToDistrictList} from "@/composables/list/regionToDistrict.list";


export default /*@__PURE__*/_defineComponent({
  __name: 'RegionSelector',
  props: {
    "selectedDistrict": {required: true},
    "selectedDistrictModifiers": {},
    "selectedRegion": {required: true},
    "selectedRegionModifiers": {},
  },
  emits: ["update:selectedDistrict", "update:selectedRegion"],
  setup(__props) {

const isDistrictListOpen = ref<boolean>(false);
const hoveredDistrictKey = ref<DistrictKey | null>(null);
const hoveredDistrictElement = ref<HTMLElement | null>(null);
const districtSelectorRef = ref<HTMLElement | null>(null);
const scrollContainer = ref<HTMLElement | null>(null);
let ps: PerfectScrollbar | null = null;

// Значение поисковой строки и список регионов для отображения.
const searchStringQuery = ref<string>("");
const regionListWithSearchQuery = computed(() => {
  const query = searchStringQuery.value.toLowerCase();
  return Object.fromEntries(
      Object.entries(citiesList).filter(([_key, regionName]) =>
          regionName.toLowerCase().includes(query)
      )
  );
});


const selectedDistrictKey = _useModel<DistrictKey | null>(__props, "selectedDistrict");
const selectedRegionKey = _useModel<RegionKey | null>(__props, "selectedRegion");

const displayedDistrictName = computed<string>(() => {
  return getDistrictName(selectedDistrictKey.value);
});

const displayedDistrictKey = computed<string>(() => {
  return selectedDistrictKey.value ?? "all";
});

const hoveredDistrictRegions = computed(() => {
  if (!hoveredDistrictKey.value) return {};
  return getRegionsByDistrict(hoveredDistrictKey.value);
});

const hasRegionsToShow = computed<boolean>(() => {
  return hoveredDistrictKey.value !== null && Object.keys(hoveredDistrictRegions.value).length > 0;
});

// Вычисляем позицию списка регионов относительно наведенного элемента
const regionSelectorStyle = computed(() => {
  if (!hoveredDistrictElement.value || !districtSelectorRef.value) return { top: "0px" };

  const elementRect = hoveredDistrictElement.value.getBoundingClientRect();
  const selectorRect = districtSelectorRef.value.getBoundingClientRect();

  // Вычисляем позицию относительно district-selector
  const topPosition = elementRect.top - selectorRect.top;

  return {
    top: `${topPosition}px`
  };
});

const toggleDistrictList = (): void => {
  isDistrictListOpen.value = !isDistrictListOpen.value;
  if (!isDistrictListOpen.value) {
    hoveredDistrictKey.value = null;
    hoveredDistrictElement.value = null;
  }
};

const handleDistrictHover = (districtKey: DistrictKey, event: Event): void => {
  hoveredDistrictKey.value = districtKey;
  hoveredDistrictElement.value = event.currentTarget as HTMLElement;
};

const handleDistrictLeave = (): void => {
  // Логика при необходимости
};

const selectDistrict = (districtKey: DistrictKey): void => {
  selectedDistrictKey.value = districtKey;
  selectedRegionKey.value = null;
  isDistrictListOpen.value = false;
  hoveredDistrictKey.value = null;
  hoveredDistrictElement.value = null;
};

const selectAll = (): void => {
  selectedDistrictKey.value = null;
  selectedRegionKey.value = null;
  isDistrictListOpen.value = false;
  hoveredDistrictKey.value = null;
  hoveredDistrictElement.value = null;
};

const selectRegion = (regionKey: RegionKey): void => {
  selectedRegionKey.value = regionKey;
  selectedDistrictKey.value = hoveredDistrictKey.value;
  isDistrictListOpen.value = false;
  hoveredDistrictKey.value = null;
  hoveredDistrictElement.value = null;
};

const handleClickOutside = (event?: MouseEvent): void => {
  if (event?.target && districtSelectorRef.value) {
    const target = event.target as Node;

    // Проверяем, был ли клик внутри выпадающего списка. (Очистка поисковой строки)
    if (districtSelectorRef.value.contains(target)) {
      return;
    }
  }

  isDistrictListOpen.value = false;
  hoveredDistrictKey.value = null;
  hoveredDistrictElement.value = null;
};

const selectRegionWithDistrict = (regionKey: RegionKey): void => {
  selectedRegionKey.value = regionKey;
  selectedDistrictKey.value = regionToDistrictList[regionKey];
  isDistrictListOpen.value = false;
  hoveredDistrictKey.value = null;
  hoveredDistrictElement.value = null;
};

// Поиск региона по поисковому запросу
const searchRegion = (reginName: string): void => {
  searchStringQuery.value = reginName;
  hoveredDistrictKey.value = null;
  hoveredDistrictElement.value = null;
};

// Perfect Scrollbar
const needsScroll = computed(() => {
  return Object.keys(regionListWithSearchQuery.value).length > 8; // порог
});

onBeforeUnmount(() => {
  if (ps) {
    ps.destroy();
    ps = null;
  }
});

// Обновляем и инициализируем скролл при изменении
watch([searchStringQuery, isDistrictListOpen, needsScroll], async () => {
  await nextTick();

  // Если список открыт и контейнер доступен
  if (isDistrictListOpen.value && scrollContainer.value) {
    // Инициализируем, если еще не инициализирован
    if (!ps) {
      ps = new PerfectScrollbar(scrollContainer.value, {
        wheelSpeed: 1,
        wheelPropagation: false,
        minScrollbarLength: 20
      });
    } else {
      // Обновляем существующий
      ps.update();
    }
  }
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  const _component_InputSearchSecondary = _resolveComponent("InputSearchSecondary")!
  const _component_ListItem = _resolveComponent("ListItem")!
  const _component_ListView = _resolveComponent("ListView")!
  const _directive_click_outside = _resolveDirective("click-outside")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["district-view", {'district-view--region': selectedRegionKey.value}]),
      onClick: toggleDistrictList
    }, [
      _createElementVNode("span", _hoisted_2, _toDisplayString(selectedRegionKey.value ? _unref(getDistrictName)(selectedDistrictKey.value) : 'Федеральный округ'), 1),
      _createElementVNode("span", _hoisted_3, _toDisplayString(selectedRegionKey.value ? _unref(getRegionName)(selectedRegionKey.value) : displayedDistrictName.value), 1),
      _createVNode(IconChevronBottom, {
        class: _normalizeClass(["selector__icon", {'selector__icon--active': isDistrictListOpen.value}])
      }, null, 8, ["class"])
    ], 2),
    (isDistrictListOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          ref_key: "districtSelectorRef",
          ref: districtSelectorRef,
          class: "district-selector"
        }, [
          _createElementVNode("div", {
            onMousedown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
            onMouseup: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"])),
            onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createVNode(_component_InputSearchSecondary, {
              "model-value": searchStringQuery.value,
              placeholder: "Поиск",
              "onUpdate:modelValue": searchRegion
            }, null, 8, ["model-value"])
          ], 32),
          (searchStringQuery.value.length === 0)
            ? (_openBlock(), _createBlock(_component_ListView, {
                key: 0,
                class: "district-selector__list"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_ListItem, {
                    item: "all",
                    "model-value": displayedDistrictKey.value,
                    class: "district-selector__item",
                    "onUpdate:modelValue": selectAll
                  }, {
                    default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                      _createTextVNode(" Все ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["model-value"]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(districtList), (districtName, districtKey) => {
                    return (_openBlock(), _createBlock(_component_ListItem, {
                      key: districtKey,
                      item: districtKey,
                      "model-value": displayedDistrictKey.value,
                      class: "district-selector__item",
                      onMouseenter: ($event: any) => (handleDistrictHover(districtKey as DistrictKey, $event)),
                      onMouseleave: handleDistrictLeave,
                      onClick: ($event: any) => (selectDistrict(districtKey as DistrictKey))
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(districtName.short) + " ", 1),
                        _createVNode(IconChevronRight, { class: "district-view__button-collapse" })
                      ]),
                      _: 2
                    }, 1032, ["item", "model-value", "onMouseenter", "onClick"]))
                  }), 128))
                ]),
                _: 1
              }))
            : (_openBlock(), _createElementBlock("div", {
                key: 1,
                ref_key: "scrollContainer",
                ref: scrollContainer,
                class: _normalizeClass(["district-selector__scrollable", {'district-selector__scrollable--limited': needsScroll.value}])
              }, [
                _createVNode(_component_ListView, null, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(regionListWithSearchQuery.value, (regionName, regionKey) => {
                      return (_openBlock(), _createBlock(_component_ListItem, {
                        key: regionKey,
                        item: regionKey,
                        "model-value": selectedRegionKey.value,
                        class: "region-selector__item",
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (selectRegionWithDistrict($event as RegionKey)))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(regionName), 1)
                        ]),
                        _: 2
                      }, 1032, ["item", "model-value"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ], 2)),
          (hasRegionsToShow.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 2,
                class: "region-selector",
                style: _normalizeStyle(regionSelectorStyle.value)
              }, [
                _createVNode(_component_ListView, null, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(hoveredDistrictRegions.value, (regionName, regionKey) => {
                      return (_openBlock(), _createBlock(_component_ListItem, {
                        key: regionKey,
                        item: regionKey,
                        "model-value": selectedRegionKey.value,
                        class: "region-selector__item",
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (selectRegion($event as RegionKey)))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(regionName), 1)
                        ]),
                        _: 2
                      }, 1032, ["item", "model-value"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ], 4))
            : _createCommentVNode("", true)
        ], 512))
      : _createCommentVNode("", true)
  ])), [
    [_directive_click_outside, handleClickOutside]
  ])
}
}

})